<template>
  <Modal
    v-model:open="isOpen"
    framed
    :title="displayTitle"
    :description="metaLabel || undefined"
    :size="ModalSize.LG"
    closable
    @after:leave="emit('close')"
  >
    <template #body>
      <div
        class="flex min-h-[50vh] items-center justify-center rounded-md bg-surface px-4 py-7"
      >
        <img
          v-if="previewMode === 'image' || previewMode === 'generated'"
          class="max-h-[60vh] max-w-full rounded-md"
          :src="imageSrc"
          :alt="displayTitle"
        />
        <video
          v-else-if="previewMode === 'video'"
          class="max-h-[60vh] max-w-full rounded-md"
          controls
          autoplay
          :src="file.fileUrl"
        ></video>
        <audio
          v-else-if="previewMode === 'audio'"
          controls
          :src="file.fileUrl"
        ></audio>
        <div
          v-else-if="previewMode === 'pdf' && pdfUrl"
          class="w-full min-w-0 overflow-x-auto"
        >
          <PdfViewerCanvas
            :renderer="pdf"
            :file-url="pdfUrl"
            :scrollable="false"
          />
        </div>
        <div
          v-else
          class="flex max-w-[30rem] flex-col items-center py-6 text-center"
        >
          <NoPreviewSvg class="h-40 w-auto" aria-hidden="true" />
          <p class="mt-6 text-2xl font-bold text-on-surface">
            {{ $t('content.file-list.no-preview-title') }}
          </p>
          <p class="mt-2 text-on-surface-variant">
            {{ $t('content.file-list.no-preview-description') }}
          </p>
          <Button
            v-if="file.showDownloadButton"
            :label="$t('content.file-list.download-to-view')"
            class="mt-6"
            @click="emit('download')"
          />
        </div>
      </div>
    </template>

    <template #footer="{ close }">
      <div class="flex w-full flex-wrap items-center justify-between gap-3">
        <Button
          :label="$t('common.close')"
          :variant="ButtonVariant.TEXT"
          :color="ButtonColor.SECONDARY"
          @click="close"
        />
        <PdfViewerControls
          v-if="previewMode === 'pdf' && !pdf.error.value"
          :renderer="pdf"
        />
        <div class="flex items-center gap-3">
          <Button
            v-if="orderInStoreUrl"
            :label="$t('content.order-item-in-store')"
            :variant="ButtonVariant.OUTLINED"
            :to="orderInStoreUrl"
          />
          <Button
            v-if="file.showDownloadButton"
            :label="$t('common.download')"
            @click="emit('download')"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  Button,
  ButtonColor,
  ButtonVariant,
  FileKind,
  PdfViewerCanvas,
  PdfViewerControls,
  formatFileDate,
  formatFileSize,
  getFileKind,
  getFileNameExtension,
  getFileNameFromUrl,
  usePdfRenderer,
  Modal,
  ModalSize,
} from '@exfluential/ui';
import { ContentDto } from '~/api/contents';
import NoPreviewSvg from '~/assets/images/placeholders/no-preview.svg?component';

type ContentFile = NonNullable<ContentDto['files']>[number];

const props = defineProps<{
  file: ContentFile;
  orderInStoreUrl?: string | null;
}>();

const emit = defineEmits<{
  close: [];
  download: [];
}>();

const isOpen = ref(true);
const pdf = usePdfRenderer();

const fileName = computed(
  () =>
    props.file.fileName ||
    getFileNameFromUrl(props.file.fileUrl) ||
    props.file.title,
);

const displayTitle = computed(() => props.file.title || fileName.value);

const metaLabel = computed(() =>
  [
    getFileNameExtension(fileName.value).toUpperCase() || null,
    props.file.fileSize ? formatFileSize(props.file.fileSize) : null,
    props.file.fileUploadedAt
      ? formatFileDate(props.file.fileUploadedAt)
      : null,
  ]
    .filter(Boolean)
    .join(' · '),
);

const generatedPreviewUrl = computed(
  () => props.file.customPreviewUrl || props.file.previewUrl || null,
);

const pdfUrl = computed(() => {
  if (getFileKind(fileName.value) === FileKind.PDF) return props.file.fileUrl;
  return props.file.renderedPdfUrl ?? null;
});

const previewMode = computed<
  'image' | 'video' | 'audio' | 'pdf' | 'generated' | 'none'
>(() => {
  if (pdfUrl.value) return 'pdf';
  switch (getFileKind(fileName.value)) {
    case FileKind.IMAGE: {
      return 'image';
    }
    case FileKind.VIDEO: {
      return 'video';
    }
    case FileKind.AUDIO: {
      return 'audio';
    }
    default: {
      return generatedPreviewUrl.value ? 'generated' : 'none';
    }
  }
});

const imageSrc = computed(() =>
  previewMode.value === 'generated'
    ? (generatedPreviewUrl.value ?? undefined)
    : props.file.fileUrl,
);
</script>
